import { Body1Strong, Button } from '@fluentui/react-components';
import { ControlledTooltip, DateLabel, SelectList } from '@shared/components';
import { EItemScreenMode, TResolvedItemsMap, useItemScreenStore } from '@modules/items';
import { AccessType, TPermissions } from '@modules/access';
import { isTDocument, TDocument, TVersion } from '@modules/documents';
import { TFile, TFileVersion } from '@modules/files';
import { useContext, useMemo } from 'react';
import ImpactCard from './ImpactCard';
import ImpactAnalysisPanel from './ImpactAnalysisPanel';
import { InfoRegular } from '@fluentui/react-icons';
import { UserLabel } from '@modules/users';
import { IMPACTS_NOTIFICATIONS } from '../constants';
import ChangingSections from './ChangingSections';
import resolveHeadings from '../helpers/resolveHeadings.helper';

type TProps = {
  item: TDocument | TFile;
  resolvedItems: TResolvedItemsMap;
  version: TVersion | TFileVersion;
  permissions: TPermissions;
};

const PostImpactSummary = ({ item, resolvedItems, version, permissions }: TProps) => {
  const { setScreenMode } = useItemScreenStore();
  const isDoc = isTDocument(item);

  const handleStartAnalysis = () => setScreenMode(EItemScreenMode.POST_CHANGE_IMPACT_ANALYSIS);

  const impactAnalysisData = (version as TVersion)?.impactAnalysis;

  /**
   * `impacts` is an array of objects where each object contains the following properties:
   * - `item`: The item that is impacted (`TItem`).
   * - `headings`: An array of headings that are impacted in the item (`THeading[] | undefined`)
   */
  const impacts = useMemo(() => {
    if (!impactAnalysisData) return [];

    const impactItems = [];

    for (const impact of impactAnalysisData.impacts) {
      const resolvedItem = resolvedItems[impact.item];

      /**
       * `resolvedItem` might be undefined if the item has been deleted.
       * In that case, we should not render the impact card.
       */
      if (!resolvedItem) continue;

      const headings = resolveHeadings(impact.headingIds, resolvedItem.resolvedHeadings);

      impactItems.push({
        item: resolvedItem.item,
        headings,
      });
    }

    return impactItems;
  }, [impactAnalysisData, resolvedItems]);

  // Users with at least edit permissions can start the post-change impact analysis
  const hasEditPermissions = permissions.accessRight >= AccessType.EDIT;

  return (
    <ImpactAnalysisPanel title={`Analyzed impacts of v${version.id}`}>
      <div className="flex flex-col gap-6">
        {!impactAnalysisData ? (
          <div className="flex flex-col gap-2 items-start">
            <ControlledTooltip
              enable={!hasEditPermissions}
              content={IMPACTS_NOTIFICATIONS.POST_CHANGE_NO_EDIT_PERMISSIONS}
              relationship="inaccessible"
              positioning="before"
              withArrow
            >
              <Button appearance="primary" size="medium" onClick={handleStartAnalysis} disabled={!hasEditPermissions}>
                Start analysis
              </Button>
            </ControlledTooltip>
            <p>
              <InfoRegular className="mb-1" /> The impact analysis has not been done yet.
            </p>
          </div>
        ) : (
          <>
            <div className="my-2">
              <UserLabel id={impactAnalysisData.user} /> analyzed the potential impacts of this change on{' '}
              <DateLabel short>{impactAnalysisData.createDate}</DateLabel>.
            </div>
            {isDoc && (
              <div>
                <p className="mb-2">Sections that have been changed in v{version.id}:</p>
                <ChangingSections
                  version={version as TVersion}
                  changedSectionIds={(version as TVersion).changedSections.map((s) => s.headingId)}
                />
              </div>
            )}

            {impacts.length <= 0 ? (
              <p>No impacts found.</p>
            ) : (
              <>
                <div>
                  <div className="mb-2 flex flex-col gap-1">
                    <Body1Strong>
                      {impacts.length} potential impact{impacts.length > 1 && 's'}
                    </Body1Strong>
                    <p>
                      The following files or sections require review and may need updates in response to this change.
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    {impacts.map((impact, idx) => (
                      <ImpactCard {...impact} key={idx} />
                    ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </ImpactAnalysisPanel>
  );
};

export default PostImpactSummary;
